import React from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import Header from '../components/Header';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="text-lg text-gray-400">
            Please read these terms carefully before using our service.
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-lg border-2 border-black">
            
            {/* Introduction */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to BandCam.io. These Terms of Service ("Terms") govern your use of our website and services 
                that connect school bands with families seeking personalized video messages for special occasions.
              </p>
              <p className="text-gray-700">
                By accessing or using our service, you agree to be bound by these Terms. If you disagree with any 
                part of these terms, you may not access our service.
              </p>
            </div>

            {/* Service Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">2. Service Description</h2>
              <p className="text-gray-700 mb-4">
                BandCam.io provides a platform that:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Connects families with school bands for personalized video messages</li>
                <li>Facilitates the creation and delivery of custom musical performances</li>
                <li>Processes payments and manages commission distribution</li>
                <li>Provides customer support and quality assurance</li>
              </ul>
            </div>

            {/* User Accounts */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">3. User Accounts</h2>
              <p className="text-gray-700 mb-4">
                When you create an account with us, you must provide accurate and complete information. You are 
                responsible for safeguarding your account credentials and for all activities that occur under your account.
              </p>
              <p className="text-gray-700">
                You agree to notify us immediately of any unauthorized use of your account or any other breach of security.
              </p>
            </div>

            {/* Band Director Responsibilities */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">4. Band Director Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                As a band director participating in our service, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Maintain appropriate permissions from your school administration</li>
                <li>Ensure all student participants have proper consent forms</li>
                <li>Deliver high-quality video recordings within specified timeframes</li>
                <li>Follow our recording guidelines and quality standards</li>
                <li>Maintain professional conduct and appropriate content</li>
                <li>Respect copyright laws and obtain necessary licenses for music performed</li>
              </ul>
            </div>

            {/* Customer Responsibilities */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">5. Customer Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                As a customer ordering video messages, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate and appropriate content for personalized messages</li>
                <li>Respect the delivery timeline and not request unreasonable modifications</li>
                <li>Use the videos for personal, non-commercial purposes only</li>
                <li>Not redistribute or sell the videos without permission</li>
                <li>Provide honest feedback and ratings</li>
              </ul>
            </div>

            {/* Payment Terms */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">6. Payment and Commission Terms</h2>
              <p className="text-gray-700 mb-4">
                <strong>Commission Structure:</strong> Band directors keep 85% of each video sale, with BandCam.io 
                retaining 15% for platform services, payment processing, and customer support.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Payment Schedule:</strong> Commissions are calculated monthly and paid within 15 days of 
                the end of each month. No minimum threshold applies.
              </p>
              <p className="text-gray-700">
                <strong>Refunds:</strong> Customers may request refunds within 7 days of video delivery if the 
                video does not meet our quality standards or delivery requirements.
              </p>
            </div>

            {/* Intellectual Property */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">7. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                <strong>Platform Content:</strong> The BandCam.io platform, including its design, functionality, 
                and content, is owned by BandCam.io and protected by intellectual property laws.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Video Content:</strong> Customers retain rights to their personalized video messages. 
                Band directors grant customers a license to use the videos for personal purposes.
              </p>
              <p className="text-gray-700">
                <strong>Music Rights:</strong> Band directors are responsible for obtaining necessary licenses 
                for any copyrighted music performed in videos.
              </p>
            </div>

            {/* Prohibited Uses */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">8. Prohibited Uses</h2>
              <p className="text-gray-700 mb-4">
                You may not use our service to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Create content that is offensive, inappropriate, or violates laws</li>
                <li>Infringe on intellectual property rights</li>
                <li>Attempt to circumvent payment systems or commission structures</li>
                <li>Harass, threaten, or harm other users</li>
                <li>Use automated systems to access our service</li>
                <li>Distribute malware or attempt to compromise our systems</li>
              </ul>
            </div>

            {/* Privacy and Data */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">9. Privacy and Data Protection</h2>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our collection and use of personal information is governed by 
                our Privacy Policy, which is incorporated into these Terms by reference.
              </p>
              <p className="text-gray-700">
                We implement appropriate security measures to protect your data and comply with applicable 
                data protection laws.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">10. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                BandCam.io shall not be liable for any indirect, incidental, special, consequential, or punitive 
                damages, including but not limited to loss of profits, data, or use.
              </p>
              <p className="text-gray-700">
                Our total liability to you for any claims arising from these Terms or your use of our service 
                shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your account immediately, without prior notice, for conduct that 
                we believe violates these Terms or is harmful to other users or our service.
              </p>
              <p className="text-gray-700">
                You may terminate your account at any time by contacting our support team. Upon termination, 
                your right to use our service will cease immediately.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of significant 
                changes via email or through our platform.
              </p>
              <p className="text-gray-700">
                Your continued use of our service after changes become effective constitutes acceptance of the new Terms.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">13. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of the state where 
                BandCam.io is incorporated, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 font-display">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                <p className="text-gray-700"><strong>Email:</strong> legal@bandcam.io</p>
                <p className="text-gray-700"><strong>Phone:</strong> (555) 123-4567</p>
                <p className="text-gray-700"><strong>Address:</strong> 123 Music Street, Suite 100, Anytown, ST 12345</p>
              </div>
            </div>

            {/* Effective Date */}
            <div className="border-t-2 border-gray-200 pt-6">
              <p className="text-sm text-gray-600 text-center">
                These Terms of Service are effective as of {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-goldie hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
